/* sugar:assets/variant-picker.js@2.0.0 */
/*
  Variant Picker engine. One copy per page, keyed off data attributes only.

  The picker embeds every variant (id, options, availability, price) in a JSON
  script, so a click resolves the variant locally and tells the rest of the page
  (price, buy button, sticky bar, media gallery) straight away — no round trip.
  product-info.js still fetches the section afterwards for the parts that need
  server HTML (SKU, inventory, quantity rules, URL), but it no longer replaces
  the picker's own markup.

  What this file owns:
    - availability: a value is "missing" when no variant carries it together
      with the other options currently picked, and "sold out" when the variants
      that do exist are all unavailable. Missing values are always disabled;
      sold-out values follow the picker's Sold-out values setting.
    - the active state, the "Selected: Black" header text, the dropdown's
      swatch/image preview, and the variant:change dispatch.
    - options the page shows no picker for (offer cards that choose the Size
      beside a Flavor picker): their value is the variant the page is buying,
      and a hidden input carries it into product-info's request, so the server
      answers with the variant the page shows instead of picking that option.
*/
(function () {
  'use strict';

  if (window.SugarVariantPicker) return;
  if (window.__swOk === false) return;

  var ROOT = '.sugar-product-variant-picker';
  var JUMP = '.sugar-variant-picker-option__jump';
  var JUMP_ROW = '.sugar-variant-picker-option__jump-row';
  var JUMP_STAY_MS = 4000;
  var JUMP_SLIDE_MS = 400;

  function parseJSON(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function Picker(root) {
    var self = this;
    this.root = root;
    this.selects = root.querySelector('variant-selects');
    this.variants = parseJSON(root.querySelector('[data-product-variants]')) || [];
    // Per option position, each value's option value id: { "1x": 123, "2x": 456 }
    this.valueIds = parseJSON(root.querySelector('[data-option-value-ids]')) || [];
    this.sectionId = root.dataset.sectionId || '';
    this.soldOutMode = root.dataset.vpSoldOut === 'clickable' ? 'clickable' : 'disabled';
    this.soldOutText = root.dataset.vpSoldOutText || 'Sold out';
    this.missingText = root.dataset.vpUnavailableText || 'Unavailable';
    root.__sugarPicker = this;

    // Capture phase: this runs before the <variant-selects> element's own
    // listener, so the option ids it sends to the server already reflect any
    // jump made here and both sides settle on the same variant.
    root.addEventListener(
      'change',
      function (e) {
        var target = e.target;
        if (!target || (target.type !== 'radio' && target.tagName !== 'SELECT')) return;
        self.onChange(target);
      },
      true
    );

    // The "Tap to see it in …" pill an option renders under its values (phones only)
    root.addEventListener('click', function (e) {
      var jump = e.target && e.target.closest && e.target.closest(JUMP);
      if (!jump || !root.contains(jump)) return;
      self.hideJump(true);
      var gallery = self.gallery();
      if (!gallery) return;
      var header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      var top = gallery.getBoundingClientRect().top + window.pageYOffset - header;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });

    this.refresh();
  }

  // The product gallery this picker should send the shopper to: the media block in the same
  // section, the classic wrapper otherwise, then anywhere on the page.
  Picker.prototype.gallery = function () {
    var scope = this.root.closest('.shopify-section') || document;
    return (
      scope.querySelector('.sugar-product-media') ||
      scope.querySelector('.product__media-wrapper') ||
      document.querySelector('.sugar-product-media') ||
      document.querySelector('.product__media-wrapper')
    );
  };

  // On a phone the gallery sits above the picker, so a colour change happens out of sight.
  // Show the holder's pill with the chosen value for four seconds — only when the gallery
  // is actually off screen; when it is visible the shopper already sees the change.
  Picker.prototype.showJump = function (holder, value) {
    var jump = holder.querySelector(JUMP);
    if (!jump || window.innerWidth >= 750) return;
    var gallery = this.gallery();
    if (!gallery) return;
    var rect = gallery.getBoundingClientRect();
    var header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    var onScreen = rect.bottom > header && rect.top < window.innerHeight && rect.height > 0;
    if (onScreen) {
      this.hideJump(true);
      return;
    }

    var self = this;
    if (this.jump && this.jump !== jump) this.hideJump(true);
    clearTimeout(this.jumpTimer);
    clearTimeout(this.jumpSlide);

    jump.textContent = (jump.dataset.vpoJumpText || '').split('[value]').join(value);
    this.jump = jump;
    var row = jump.closest(JUMP_ROW) || jump;
    if (row.hidden) {
      row.hidden = false;
      // A layout pass between unhiding and opening is what lets the slide-in animate
      void row.offsetHeight;
    }
    row.classList.add('is-open');
    this.jumpTimer = setTimeout(function () {
      self.hideJump(false);
    }, JUMP_STAY_MS);
  };

  // Slide the open pill shut, or drop it at once (a tap, a new change elsewhere)
  Picker.prototype.hideJump = function (immediate) {
    var jump = this.jump;
    clearTimeout(this.jumpTimer);
    clearTimeout(this.jumpSlide);
    this.jumpTimer = null;
    this.jump = null;
    if (!jump) return;
    var row = jump.closest(JUMP_ROW) || jump;
    row.classList.remove('is-open');
    if (immediate) {
      row.hidden = true;
      return;
    }
    this.jumpSlide = setTimeout(function () {
      if (!row.classList.contains('is-open')) row.hidden = true;
    }, JUMP_SLIDE_MS);
  };

  Picker.prototype.holders = function () {
    return Array.prototype.slice.call(this.root.querySelectorAll('[data-option-position]:not([data-vp-carry])'));
  };

  // Other pickers for the same product in the same section: a page can split the
  // options across blocks (a size picker, a note, then a colour picker). They
  // share one selection, so each reads the positions it lacks from the others
  // and follows a change made in any of them.
  Picker.prototype.siblings = function () {
    var self = this;
    var scope = this.root.closest('.shopify-section') || document;
    var first = this.variants[0];
    return Array.prototype.slice
      .call(scope.querySelectorAll(ROOT))
      .map(function (root) {
        return root.__sugarPicker;
      })
      .filter(function (picker) {
        if (!picker || picker === self || picker.sectionId !== self.sectionId) return false;
        var other = picker.variants[0];
        return !!(first && other && String(first.id) === String(other.id));
      });
  };

  function readHolders(holders, picked) {
    holders.forEach(function (holder) {
      var index = parseInt(holder.dataset.optionPosition, 10);
      if (!Number.isFinite(index) || picked[index] != null) return;
      var select = holder.querySelector('select');
      var value = select ? select.value : (holder.querySelector('input[type="radio"]:checked') || {}).value;
      if (value != null) picked[index] = value;
    });
  }

  // The value picked for every option position, from the live inputs — this
  // picker's first, then any sibling picker's for the positions it lacks, then
  // the variant the page is buying for the options no picker shows.
  Picker.prototype.selected = function () {
    var picked = [];
    readHolders(this.holders(), picked);
    this.siblings().forEach(function (picker) {
      readHolders(picker.holders(), picked);
    });
    var unshown = this.unshown();
    if (unshown.length) {
      var current = this.current();
      if (current && Array.isArray(current.options)) {
        unshown.forEach(function (index) {
          if (picked[index] == null && current.options[index] != null) picked[index] = current.options[index];
        });
      }
    }
    return picked;
  };

  // Option positions no picker in this section shows. A page whose offer cards
  // choose the Size shows only the Flavor picker; a normal picker shows them all.
  Picker.prototype.unshown = function () {
    var count = this.variants.length && Array.isArray(this.variants[0].options) ? this.variants[0].options.length : 0;
    if (!count) return [];
    var shown = {};
    var mark = function (holder) {
      var index = parseInt(holder.dataset.optionPosition, 10);
      if (Number.isFinite(index)) shown[index] = true;
    };
    this.holders().forEach(mark);
    this.siblings().forEach(function (picker) {
      picker.holders().forEach(mark);
    });
    var out = [];
    for (var i = 0; i < count; i++) {
      if (!shown[i]) out.push(i);
    }
    return out;
  };

  // The variant the page is buying: the one in the buy button's form, which the
  // offer cards write when a card is chosen, else the one the page rendered.
  Picker.prototype.current = function () {
    var form = this.findForm();
    var input = form && form.querySelector('input[name="id"]');
    var id = input ? String(input.value) : '';
    var hit = id
      ? this.variants.find(function (variant) {
          return String(variant.id) === id;
        })
      : null;
    return hit || parseJSON(this.root.querySelector('[data-selected-variant]'));
  };

  // One hidden input per unshown option inside this picker's <variant-selects>.
  // The section request product-info sends after a change is built from the
  // inputs there; naming only the shown options let the server choose the rest
  // (a Flavor change answered with the 2x variant while the 1x card stayed
  // selected, and the address bar followed the server).
  Picker.prototype.carry = function (variant) {
    if (!this.selects || !variant || !Array.isArray(variant.options)) return;
    var self = this;
    var keep = {};
    this.unshown().forEach(function (index) {
      var value = variant.options[index];
      var ids = self.valueIds[index];
      var id = ids && value != null ? ids[value] : null;
      if (id == null) return;
      keep[index] = true;
      var holder = self.selects.querySelector('[data-vp-carry][data-option-position="' + index + '"]');
      if (!holder) {
        holder = document.createElement('fieldset');
        holder.hidden = true;
        holder.setAttribute('data-vp-carry', '');
        holder.setAttribute('data-option-position', String(index));
        var created = document.createElement('input');
        created.type = 'radio';
        created.tabIndex = -1;
        holder.appendChild(created);
        self.selects.appendChild(holder);
      }
      var input = holder.querySelector('input');
      input.value = value;
      input.setAttribute('data-option-value-id', String(id));
      input.checked = true;
    });
    Array.prototype.forEach.call(this.selects.querySelectorAll('[data-vp-carry]'), function (holder) {
      if (!keep[holder.getAttribute('data-option-position')]) holder.remove();
    });
  };

  Picker.prototype.matchExact = function (picked) {
    var count = this.variants.length ? this.variants[0].options.length : 0;
    for (var i = 0; i < count; i++) {
      if (picked[i] == null) return null;
    }
    return (
      this.variants.find(function (variant) {
        return variant.options.every(function (option, i) {
          return option === picked[i];
        });
      }) || null
    );
  };

  // For one option value: does any variant carry it alongside everything else
  // that is picked, and is any of those variants purchasable?
  Picker.prototype.stateFor = function (index, value, picked) {
    var exists = false;
    var available = false;
    for (var v = 0; v < this.variants.length; v++) {
      var variant = this.variants[v];
      if (variant.options[index] !== value) continue;
      var fits = true;
      for (var j = 0; j < variant.options.length; j++) {
        if (j === index || picked[j] == null) continue;
        if (variant.options[j] !== picked[j]) {
          fits = false;
          break;
        }
      }
      if (!fits) continue;
      exists = true;
      if (variant.available) {
        available = true;
        break;
      }
    }
    return { exists: exists, available: available };
  };

  // The variant that keeps `value` at `index` and matches the rest of the pick
  // as closely as possible, in stock first. Options no picker shows stay where
  // they are whenever a variant allows it: the shopper chose them elsewhere (an
  // offer card), and this picker has no way to show that it moved them.
  Picker.prototype.nearest = function (index, value, picked, fixed) {
    var candidates = this.variants.filter(function (variant) {
      return variant.options[index] === value;
    });
    if (!candidates.length) return null;
    if (fixed && fixed.length) {
      var kept = candidates.filter(function (variant) {
        return fixed.every(function (i) {
          return picked[i] == null || variant.options[i] === picked[i];
        });
      });
      if (kept.length) candidates = kept;
    }
    var score = function (variant) {
      return variant.options.reduce(function (sum, option, i) {
        return sum + (i !== index && option === picked[i] ? 1 : 0);
      }, 0);
    };
    return candidates.slice().sort(function (a, b) {
      if (a.available !== b.available) return a.available ? -1 : 1;
      return score(b) - score(a);
    })[0];
  };

  // Reflect a variant in the inputs, the active states and the header text —
  // without firing change events.
  Picker.prototype.applySelection = function (variant) {
    if (!variant || !Array.isArray(variant.options)) return;
    var self = this;
    this.holders().forEach(function (holder) {
      var index = parseInt(holder.dataset.optionPosition, 10);
      if (!Number.isFinite(index)) return;
      var value = variant.options[index];
      if (value == null) return;
      var select = holder.querySelector('select');
      if (select) {
        if (select.value !== value) select.value = value;
        self.updateDropdownVisual(holder);
      } else {
        holder.querySelectorAll('[data-value]').forEach(function (node) {
          var on = node.dataset.value === value;
          node.classList.toggle('is-active', on);
          var input = node.querySelector('input[type="radio"]');
          if (input && input.checked !== on) input.checked = on;
        });
      }
      self.updateHeader(holder, value);
    });
  };

  // Amazon-style: the greyed look means "not with the options picked so far",
  // and clicking such a value jumps the other options to a variant that has it
  // (onChange → nearest). A value is only truly disabled when there is nowhere
  // to jump: no variant carries it at all, or — with sold-out values disabled —
  // none in stock does.
  Picker.prototype.refresh = function () {
    var picked = this.selected();
    var self = this;
    var none = [];
    this.holders().forEach(function (holder) {
      var index = parseInt(holder.dataset.optionPosition, 10);
      if (!Number.isFinite(index)) return;

      holder.querySelectorAll('[data-value]').forEach(function (node) {
        var state = self.stateFor(index, node.dataset.value, picked);
        var soldOut = state.exists && !state.available;
        var anywhere = self.stateFor(index, node.dataset.value, none);
        var disabled = self.soldOutMode === 'disabled' ? !anywhere.available : !anywhere.exists;

        if (node.tagName === 'OPTION') {
          node.disabled = disabled;
          var label = node.dataset.label || node.dataset.value;
          if (!state.exists) label += ' - ' + self.missingText;
          else if (soldOut) label += ' - ' + self.soldOutText;
          if (node.textContent !== label) node.textContent = label;
          return;
        }

        node.classList.toggle('is-missing', !state.exists);
        node.classList.toggle('is-unavailable', soldOut);
        var input = node.querySelector('input[type="radio"]');
        if (input) input.disabled = disabled;
      });
    });
  };

  Picker.prototype.updateHeader = function (holder, value) {
    var target = holder.querySelector('[data-selected-value]');
    if (!target) return;
    // Value groups: the selected value sits in the heading of the group that holds it, or in
    // the option's header when that group has no name (the header shows only while it does).
    if (holder.querySelector('.sugar-variant-picker-option__group-name--with-value')) {
      var home = null;
      holder.querySelectorAll('.sugar-variant-picker-option__group').forEach(function (group) {
        if (home) return;
        var hit = Array.prototype.some.call(group.querySelectorAll('[data-value]'), function (node) {
          return node.dataset.value === String(value);
        });
        if (hit) home = group.querySelector('.sugar-variant-picker-option__group-name') || 'header';
      });
      var header = holder.querySelector('.sugar-variant-picker-option__header');
      if (home === 'header' || !home) {
        if (header) {
          var name = header.querySelector('.sugar-variant-picker-option__name');
          if (target.parentNode !== header) header.insertBefore(target, name ? name.nextSibling : header.firstChild);
          if (header.hidden) header.hidden = false;
        }
      } else if (target.parentNode !== home) {
        home.appendChild(target);
        if (header && !Array.prototype.some.call(header.children, function (el) { return !/^(LINK|SCRIPT|STYLE|TEMPLATE)$/.test(el.tagName); })) header.hidden = true;
      }
    }
    var template = target.dataset.selectedTemplate || '[variant]';
    target.innerHTML = template
      .split('[variant_uppercase]').join(escapeHtml(String(value).toUpperCase()))
      .split('[variant]').join(escapeHtml(value));
  };

  Picker.prototype.updateDropdownVisual = function (holder) {
    var select = holder.querySelector('select');
    var visual = holder.querySelector('[data-dropdown-visual]');
    if (!select || !visual) return;
    var option = select.options[select.selectedIndex];
    if (!option) return;
    var type = option.dataset.visualType;
    var value = option.dataset.visualValue;
    if (type === 'image' && value) {
      visual.innerHTML =
        '<img class="sugar-variant-picker-option__dropdown-img" src="' +
        escapeHtml(value) +
        '" alt="" loading="lazy">';
    } else if (type === 'color' && value) {
      visual.innerHTML =
        '<span class="sugar-variant-picker-option__dropdown-swatch" style="background:' +
        escapeHtml(value) +
        '"></span>';
    } else {
      visual.innerHTML = '';
    }
  };

  /*
   * Nearest form first. A section can hold more than one buy button — the Custom Sticky's
   * desktop and mobile bars each carry their own, under the same section-derived id — and a
   * picker belongs to the button beside it. getElementById always answered with whichever
   * rendered first, so a shopper choosing a variant in the mobile bar wrote it into the
   * hidden desktop form and added the variant they had not picked.
   */
  Picker.prototype.findForm = function () {
    var section = this.root.closest('.shopify-section');
    var stop = section ? section.parentElement : null;
    for (var el = this.root.parentElement; el && el !== stop; el = el.parentElement) {
      var near =
        el.querySelector('form[data-type="add-to-cart-form"]') ||
        el.querySelector('product-form form') ||
        el.querySelector('form[action*="/cart/add"]');
      if (near) return near;
    }
    return document.getElementById('product-form-' + this.sectionId);
  };

  Picker.prototype.dispatch = function (variant) {
    var sectionId = this.sectionId;

    var form = this.findForm();
    var idInput = form && form.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = variant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Keep the embedded "current variant" fresh for anything that reads it
    // later (the media gallery on init, product-info's fallback scoring).
    var current = this.root.querySelector('[data-selected-variant]');
    if (current) current.textContent = JSON.stringify(variant);

    var detail = { sectionId: sectionId, variant: variant };
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        publish(PUB_SUB_EVENTS.variantChange, { data: detail });
      } catch (e) {
        /* a subscriber threw; the DOM event below still goes out */
      }
    }
    this.dispatching = true;
    try {
      document.dispatchEvent(new CustomEvent('variant:change', { detail: detail }));
    } finally {
      this.dispatching = false;
    }
  };

  Picker.prototype.onChange = function (input) {
    var holder = input.closest('[data-option-position]');
    if (!holder) return;
    var index = parseInt(holder.dataset.optionPosition, 10);

    var picked = this.selected();
    var unshown = this.unshown();
    var exact = this.matchExact(picked);
    var target = exact;

    // Changing one option can land on a combination that doesn't exist or is
    // sold out. With sold-out values disabled, move the other options to the
    // nearest in-stock variant that keeps what was just picked.
    if (!exact || (!exact.available && this.soldOutMode === 'disabled')) {
      var near = this.nearest(index, input.value, picked, unshown);
      if (near) target = near;
    }

    if (target) {
      this.applySelection(target);
      this.siblings().forEach(function (picker) {
        picker.applySelection(target);
        picker.refresh();
      });
      // Before the <variant-selects> listener builds the server request
      if (unshown.length) {
        this.carry(target);
        this.siblings().forEach(function (picker) {
          picker.carry(target);
        });
      }
      this.dispatch(target);
    } else {
      this.updateHeader(holder, input.value);
      this.updateDropdownVisual(holder);
    }

    this.refresh();

    // Only the option the shopper touched offers the jump; siblings merely followed
    this.showJump(holder, target ? target.options[index] : input.value);
  };

  function scan(scope) {
    var within = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(within.querySelectorAll(ROOT), function (root) {
      if (root.__sugarPicker) root.__sugarPicker.refresh();
      else new Picker(root);
    });
  }

  // product-info.js publishes the server's answer after its section fetch; if
  // it settled on a different variant, the inputs follow it. A change from
  // anywhere else (an offer card choosing the Size no picker shows) changes
  // which of the shown values exist and are in stock with it.
  document.addEventListener('variant:change', function (e) {
    var detail = e.detail;
    if (!detail || !detail.variant) return;
    Array.prototype.forEach.call(document.querySelectorAll(ROOT), function (root) {
      var picker = root.__sugarPicker;
      if (!picker || picker.sectionId !== detail.sectionId) return;
      if (!detail.html) {
        if (!picker.dispatching && picker.unshown().length) picker.refresh();
        return;
      }
      var picked = picker.selected();
      var current = picker.matchExact(picked);
      if (current && String(current.id) === String(detail.variant.id)) return;
      picker.applySelection(detail.variant);
      picker.refresh();
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
  });

  window.SugarVariantPicker = { scan: scan };
})();
